import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { get } from '../../lib/apiClient';
import { listVendorInvoices } from '../../lib/vendorInvoiceApi';
import { fetchVendorPurchaseOrders } from '../../lib/vendorPurchaseOrderApi';
import {
  traceClientInvoiceSource,
  type SourceTraceQuery,
  type SourceTraceResult,
} from '../../lib/clientInvoiceApi';
import { formatDate as sharedFormatDate } from "../../utils/dateFormat";

/**
 * Vendor Source Traceability — the reverse trace.
 *
 * A complaint arrives pointing at a supplier or a supplier's invoice, never at
 * a client invoice line. This answers it in that direction: pick the source,
 * get every vessel and line that received goods from it.
 *
 * Internal only — this names suppliers, which never appears on client-facing
 * documents.
 */

type Mode = 'vendor' | 'vendor_invoice' | 'purchase_order';

const MODES: Array<{ id: Mode; label: string; hint: string }> = [
  { id: 'vendor', label: 'By Vendor', hint: 'Everything ever sourced from one supplier' },
  { id: 'vendor_invoice', label: 'By Vendor Invoice', hint: 'One specific supplier invoice' },
  { id: 'purchase_order', label: 'By Purchase Order', hint: 'One order we placed' },
];

type Candidate = { id: string; label: string; sub: string };

// Was yyyy-mm-dd, which is unambiguous but is not what the rest of the system
// prints, and this panel is read side by side with the invoice screens.
const fmtDate = (value: string | null) => sharedFormatDate(value, '—');

export const SourceTracePanel: React.FC<{
  downloadCsv: (filename: string, rows: Record<string, unknown>[]) => void;
}> = ({ downloadCsv }) => {
  const [mode, setMode] = useState<Mode>('vendor_invoice');
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [picked, setPicked] = useState<Candidate | null>(null);
  const [result, setResult] = useState<SourceTraceResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [tracing, setTracing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Changing mode invalidates everything downstream of it.
  useEffect(() => {
    setSearch('');
    setCandidates([]);
    setPicked(null);
    setResult(null);
    setError(null);
  }, [mode]);

  const loadCandidates = useCallback(async (term: string, forMode: Mode) => {
    setSearching(true);
    setError(null);
    try {
      if (forMode === 'vendor') {
        const query = term ? `?search=${encodeURIComponent(term)}` : '';
        const res: any = await get(`/vendors${query}`);
        const rows: any[] = res?.data ?? [];
        setCandidates(
          rows.slice(0, 25).map((v) => ({
            id: String(v._id),
            label: String(v.name || 'Unnamed vendor'),
            sub: v.code ? `Code ${v.code}` : 'No code',
          }))
        );
      } else if (forMode === 'vendor_invoice') {
        // listVendorInvoices resolves to { items, page, limit, total }.
        const res = await listVendorInvoices({ page: 1, limit: 25, q: term });
        setCandidates(
          (res.items ?? []).map((vi: any) => ({
            id: String(vi._id),
            label: String(vi?.extracted?.invoice?.invoiceNumber || '(no invoice number)'),
            sub: String(vi?.extracted?.vendor?.name || 'Unknown vendor'),
          }))
        );
      } else {
        // fetchVendorPurchaseOrders resolves to { vendorPurchaseOrders, pagination }.
        const res = await fetchVendorPurchaseOrders(1, 25, term);
        setCandidates(
          (res.vendorPurchaseOrders ?? []).map((po: any) => ({
            id: String(po._id),
            label: String(po.poNumber || '(no PO number)'),
            sub: String(po.vendorName || 'Unknown vendor'),
          }))
        );
      }
    } catch (e: any) {
      setError(e?.message || 'Could not load options.');
      setCandidates([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced so typing a vendor name does not fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => loadCandidates(search, mode), 300);
    return () => clearTimeout(handle);
  }, [search, mode, loadCandidates]);

  const runTrace = useCallback(
    async (candidate: Candidate) => {
      setPicked(candidate);
      setTracing(true);
      setError(null);
      setResult(null);
      try {
        const query: SourceTraceQuery =
          mode === 'vendor'
            ? { vendorId: candidate.id }
            : mode === 'vendor_invoice'
            ? { vendorInvoiceId: candidate.id }
            : { poId: candidate.id };
        setResult(await traceClientInvoiceSource(query));
      } catch (e: any) {
        setError(e?.message || 'Trace failed.');
      } finally {
        setTracing(false);
      }
    },
    [mode]
  );

  const csvRows = useMemo(
    () =>
      (result?.rows ?? []).map((r) => ({
        'Client Invoice': r.invoiceNumber,
        Date: fmtDate(r.invoiceDate),
        Status: r.status,
        Client: r.clientName,
        Vessel: r.vesselName,
        Line: r.lineNumber ?? '',
        Item: r.itemName,
        'Qty From Source': r.quantityFromSource,
        'Line Qty': r.lineQuantity,
        Unit: r.unit,
        Vendor: r.vendorName,
        'Vendor Code': r.vendorCode,
        PO: r.poNumber,
        'Vendor Invoices': r.vendorInvoices.map((vi) => vi.invoiceNumber).filter(Boolean).join('; '),
        Evidence: r.derivedFrom,
      })),
    [result]
  );

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Source Trace</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              A complaint names a supplier, not a line. Pick the source and see every vessel that
              received goods from it.
            </p>
          </div>
          {result && result.rows.length > 0 && (
            <button
              onClick={() => downloadCsv(`source-trace-${picked?.label || 'result'}.csv`, csvRows)}
              className="shrink-0 px-3 py-1.5 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:bg-slate-50"
            >
              Export CSV
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {MODES.map((m) => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              title={m.hint}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${
                mode === m.id
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            mode === 'vendor'
              ? 'Search vendors (e.g. Al Brasco)'
              : mode === 'vendor_invoice'
              ? 'Search vendor invoices by number or vendor'
              : 'Search purchase orders by number'
          }
          className="w-full h-9 px-3 rounded-md border border-slate-200 text-xs"
        />

        <div className="mt-2 max-h-40 overflow-auto rounded-md border border-slate-100">
          {searching && <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>}
          {!searching && candidates.length === 0 && (
            <div className="px-3 py-2 text-xs text-slate-400">No matches.</div>
          )}
          {candidates.map((c) => (
            <button
              key={c.id}
              onClick={() => runTrace(c)}
              className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 last:border-b-0 hover:bg-slate-50 ${
                picked?.id === c.id ? 'bg-cyan-50' : ''
              }`}
            >
              <span className="font-semibold text-slate-700">{c.label}</span>
              <span className="text-slate-400"> · {c.sub}</span>
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
          {error}
        </div>
      )}

      {tracing && (
        <div className="rounded-lg border border-slate-200 bg-white px-4 py-8 text-center text-xs text-slate-400">
          Tracing…
        </div>
      )}

      {result && !tracing && (
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[
              ['Lines', result.totals.lines],
              ['Client Invoices', result.totals.clientInvoices],
              ['Vessels', result.totals.vessels],
              ['Total Qty', result.totals.quantity],
            ].map(([label, value]) => (
              <div key={String(label)} className="rounded-md border border-slate-200 px-3 py-2">
                <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
                <div className="text-sm font-semibold text-slate-900">{value}</div>
              </div>
            ))}
          </div>

          {result.vessels.length > 0 && (
            <div className="mb-4 text-xs text-slate-600">
              <span className="font-semibold">Vessels affected: </span>
              {result.vessels.join(', ')}
            </div>
          )}

          <div className="overflow-auto rounded-lg border border-slate-200">
            <table className="data-table data-table-dense">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Invoice', 'Date', 'Vessel', 'Client', 'Line', 'Item', 'Qty', 'Vendor', 'PO', 'Vendor Invoice', 'Evidence'].map(
                    (h) => (
                      <th                         key={h}
                        className="px-3 py-2 text-left font-semibold text-slate-600 whitespace-nowrap"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {result.rows.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-slate-400">
                      Nothing was sourced from this. Invoices saved before traceability was added
                      carry no provenance, so they will not appear here.
                    </td>
                  </tr>
                )}
                {result.rows.map((r, i) => (
                  <tr key={`${r.clientInvoiceId}-${r.lineNumber}-${i}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <a
                        href={`/client-invoices/${r.clientInvoiceId}`}
                        className="text-primary-600 font-semibold hover:underline"
                      >
                        {r.invoiceNumber || '(draft)'}
                      </a>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtDate(r.invoiceDate)}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">{r.vesselName || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{r.clientName || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-500">{r.lineNumber ?? '—'}</td>
                    <td className="px-3 py-2 text-slate-700">{r.itemName || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                      {r.quantityFromSource}
                      {r.quantityFromSource !== r.lineQuantity && (
                        <span className="text-slate-400"> / {r.lineQuantity}</span>
                      )}{' '}
                      {r.unit}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-600">
                      {r.vendorCode ? `${r.vendorCode} · ` : ''}
                      {r.vendorName || '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-500">{r.poNumber || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-500">
                      {r.vendorInvoices.length
                        ? r.vendorInvoices.map((vi) => vi.invoiceNumber || '(no number)').join(', ')
                        : '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {/* How much the row can be relied on, spelled out rather
                          than left for the reader to infer from a code. */}
                      <span
                        className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                          r.derivedFrom === 'manual'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-emerald-100 text-emerald-700'
                        }`}
                      >
                        {r.derivedFrom === 'manual'
                          ? 'typed'
                          : r.derivedFrom === 'po_trail'
                          ? 'from PO'
                          : r.derivedFrom === 'po_match'
                          ? 'PO confirmed'
                          : r.derivedFrom}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
